import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiBarChart2, FiPieChart, FiTrendingUp, FiFilter } from 'react-icons/fi';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { LoadingGrid } from '../../components/UI/Loading';
import { vendedoresService } from '../../services';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const VendedorGrafica = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('general'); // 'general' o 'cliente'
  const [selectedClient, setSelectedClient] = useState(null);
  const [availableClients, setAvailableClients] = useState([]);
  const [clientChartData, setClientChartData] = useState(null);

  useEffect(() => {
    loadChartData();
    loadClientesList();
  }, [id]);

  useEffect(() => {
    if (viewMode === 'cliente' && selectedClient) {
      loadClientChartData();
    }
  }, [selectedClient]);

  const loadChartData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await vendedoresService.getGraficaVentas(id, {
        period: 'month'
      });

      if (result.success) {
        setChartData(result.data);
      } else {
        setError(result.message || 'Error al cargar las gráficas');
      }
    } catch (err) {
      console.error('Error cargando gráficas:', err);
      setError('Error de conexión al cargar las gráficas');
    } finally {
      setIsLoading(false);
    }
  };

  const loadClientesList = async () => {
    try {
      const result = await vendedoresService.getClientesVendedor(id);

      if (result.success && result.data) {
        // Transformar los datos para que tengan el formato esperado
        const clientesFormateados = result.data.map(cliente => ({
          client_id: cliente.id,
          client_name: cliente.company_name || cliente.full_name || `Cliente #${cliente.id}`
        }));
        setAvailableClients(clientesFormateados);
      }
    } catch (err) {
      console.error('Error cargando lista de clientes:', err);
    }
  };

  const loadClientChartData = async () => {
    if (!selectedClient) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await vendedoresService.getGraficaVentas(id, {
        period: 'month',
        client_id: selectedClient
      });

      if (result.success) {
        setClientChartData(result.data);
      } else {
        setError(result.message || 'Error al cargar las gráficas del cliente');
      }
    } catch (err) {
      console.error('Error cargando gráficas del cliente:', err);
      setError('Error de conexión al cargar las gráficas del cliente');
    } finally {
      setIsLoading(false);
    }
  };

  // Preparar datos para gráfica de ventas por período
  const getSalesByPeriodChartData = () => {
    if (!chartData || !chartData.sales_by_period || chartData.sales_by_period.length === 0) {
      return null;
    }

    const labels = chartData.sales_by_period.map(item => item.period);
    const data = chartData.sales_by_period.map(item => parseFloat(item.total_amount || 0));

    return {
      labels,
      datasets: [
        {
          label: 'Ventas ($)',
          data,
          backgroundColor: 'rgba(49, 130, 206, 0.2)',
          borderColor: 'rgba(49, 130, 206, 1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  // Preparar datos para gráfica de top clientes
  const getTopClientsChartData = () => {
    if (!chartData || !chartData.top_clients || chartData.top_clients.length === 0) {
      return null;
    }

    const labels = chartData.top_clients.map(client => client.client_name);
    const data = chartData.top_clients.map(client => parseFloat(client.total_amount || 0));

    const colors = [
      'rgba(16, 185, 129, 0.8)',
      'rgba(59, 130, 246, 0.8)',
      'rgba(245, 158, 11, 0.8)',
      'rgba(239, 68, 68, 0.8)',
      'rgba(139, 92, 246, 0.8)'
    ];

    return {
      labels,
      datasets: [
        {
          label: 'Ventas por Cliente ($)',
          data,
          backgroundColor: colors.slice(0, data.length),
          borderColor: colors.slice(0, data.length).map(c => c.replace('0.8', '1')),
          borderWidth: 1
        }
      ]
    };
  };

  // Preparar datos para gráfica de cliente específico
  const getClientSalesChartData = () => {
    if (!clientChartData || !clientChartData.sales_by_period || clientChartData.sales_by_period.length === 0) {
      return null;
    }

    const labels = clientChartData.sales_by_period.map(item => item.period);
    const data = clientChartData.sales_by_period.map(item => parseFloat(item.total_amount || 0));

    return {
      labels,
      datasets: [
        {
          label: 'Ventas ($)',
          data,
          backgroundColor: 'rgba(16, 185, 129, 0.2)',
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  // Opciones comunes para las gráficas
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += '$' + context.parsed.y.toLocaleString();
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString();
          }
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function(context) {
            let label = context.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed !== null) {
              label += '$' + context.parsed.toLocaleString();
            }
            return label;
          }
        }
      }
    }
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando gráficas de ventas..." />;
  }

  return (
    <div className="vendedor-grafica-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/vendedores')}
          type="button"
        >
          <FiArrowLeft /> Volver a Vendedores
        </button>

        <h1 className="form-title">
          GRÁFICA DE VENTAS
        </h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ margin: '20px' }}>
          <strong>Error:</strong> {error}
          <button
            className="btn btn--ghost btn--sm"
            onClick={loadChartData}
            style={{ marginLeft: '10px' }}
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Filtros */}
      <div className="page-content" style={{ paddingBottom: '100px' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiFilter />
            <span style={{ fontWeight: '600' }}>Vista:</span>
          </div>

          <div className="btn-group">
            <button
              className={`btn ${viewMode === 'general' ? 'btn--primary' : 'btn--ghost'}`}
              onClick={() => setViewMode('general')}
              type="button"
            >
              General
            </button>
            <button
              className={`btn ${viewMode === 'cliente' ? 'btn--primary' : 'btn--ghost'}`}
              onClick={() => setViewMode('cliente')}
              type="button"
            >
              Por Cliente
            </button>
          </div>

          {viewMode === 'cliente' && (
            <>
              <div style={{
                height: '1px',
                width: '100%',
                background: '#e5e7eb',
                margin: '0 10px'
              }}></div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                <label style={{ fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Seleccione un cliente:
                </label>
                <select
                  className="form-input"
                  value={selectedClient || ''}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  style={{ flex: 1, minWidth: '250px' }}
                >
                  <option value="">-- Seleccionar cliente --</option>
                  {availableClients.map((client) => (
                    <option key={client.client_id} value={client.client_id}>
                      {client.client_name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        {/* Vista General */}
        {viewMode === 'general' && (
          <>
            {/* Gráfica principal - Ventas por Período */}
            <div className="chart-container" style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '20px',
              minHeight: '400px'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#1a202c',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FiBarChart2 color="#3182CE" />
                Ventas por Período
              </h3>
              <div style={{ height: '350px' }}>
                {getSalesByPeriodChartData() ? (
                  <Line data={getSalesByPeriodChartData()} options={chartOptions} />
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                  }}>
                    <div style={{ textAlign: 'center', color: '#999' }}>
                      <FiBarChart2 size={60} style={{ opacity: 0.3, marginBottom: '10px' }} />
                      <p>No hay datos de ventas disponibles</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Gráficas secundarias */}
            <div className="charts-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {/* Top Clientes */}
              <div className="chart-card" style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                minHeight: '300px'
              }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1a202c',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <FiPieChart color="#10B981" />
                  Top 5 Clientes
                </h3>
                <div style={{ height: '250px' }}>
                  {getTopClientsChartData() ? (
                    <Doughnut data={getTopClientsChartData()} options={doughnutOptions} />
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%'
                    }}>
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        <FiPieChart size={60} style={{ opacity: 0.3, marginBottom: '10px' }} />
                        <p>No hay datos de clientes</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tendencia */}
              <div className="chart-card" style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                minHeight: '300px'
              }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1a202c',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <FiTrendingUp color="#F59E0B" />
                  Tendencia de Ventas
                </h3>
                <div style={{ height: '250px' }}>
                  {getSalesByPeriodChartData() ? (
                    <Bar data={getSalesByPeriodChartData()} options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        legend: {
                          display: false
                        }
                      }
                    }} />
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%'
                    }}>
                      <div style={{ textAlign: 'center', color: '#999' }}>
                        <FiTrendingUp size={60} style={{ opacity: 0.3, marginBottom: '10px' }} />
                        <p>No hay datos de tendencia</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Vista Por Cliente */}
        {viewMode === 'cliente' && (
          <div className="chart-container" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minHeight: '400px'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1a202c',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FiBarChart2 color="#3182CE" />
              {selectedClient
                ? `Ventas - ${availableClients.find(c => c.client_id === parseInt(selectedClient))?.client_name || 'Cliente'}`
                : 'Seleccione un cliente'}
            </h3>
            <div style={{ height: '350px' }}>
              {selectedClient ? (
                getClientSalesChartData() ? (
                  <Line data={getClientSalesChartData()} options={chartOptions} />
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%'
                  }}>
                    <div style={{ textAlign: 'center', color: '#999' }}>
                      <FiBarChart2 size={60} style={{ opacity: 0.3, marginBottom: '10px' }} />
                      <p>No hay datos de ventas para este cliente</p>
                    </div>
                  </div>
                )
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}>
                  <div style={{ textAlign: 'center', color: '#999' }}>
                    <FiFilter size={60} style={{ opacity: 0.3, marginBottom: '10px' }} />
                    <p>Seleccione un cliente del filtro superior para ver su gráfica de ventas</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Menú inferior */}
      <BottomTabMenu
        baseRoute={`/system/vendedores/${id}`}
        currentTab="grafica"
        tabs={[
          { id: 'datos', label: 'Datos', route: '' },
          { id: 'ventas', label: 'Reporte de Ventas', route: '/ventas' },
          { id: 'comisiones', label: 'Reporte de Comisiones', route: '/comisiones' },
          { id: 'clientes', label: 'Lista de Clientes', route: '/clientes' },
          { id: 'grafica', label: 'Gráfica de Ventas', route: '/grafica' },
          { id: 'notas', label: 'Notas', route: '/notas' }
        ]}
      />
    </div>
  );
};

export default VendedorGrafica;
